import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS, BRAND, LOGOS } from "../../lib/site-data";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[border-color,box-shadow] duration-500 glass border-b ${
        scrolled
          ? "border-[rgba(10,61,122,0.16)] shadow-[0_8px_30px_-15px_rgba(10,61,122,0.25)]"
          : "border-[rgba(10,61,122,0.08)]"
      }`}
    >
      <div className="container-x flex items-center justify-between py-4 md:py-5">
        {/* Wordmark */}
        <Link
          to="/"
          data-testid="nav-home-logo"
          aria-label="Ocean Oasis — Home"
          className="group flex items-center gap-3"
        >
          <img
            src={LOGOS.brand}
            alt="Ocean Oasis logo"
            className="h-11 md:h-12 w-auto object-contain shrink-0"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-xl md:text-2xl font-semibold tracking-tight text-[#0a3d7a]">
              {BRAND.name}
            </span>
            <span className="font-label text-[0.62rem] text-[#f59e0b] mt-0.5">
              Mogadishu · Somalia
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.href}
              to={link.href}
              end={link.href === "/"}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 font-body text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive
                    ? "text-[#f59e0b]"
                    : "text-[#0a3d7a] hover:text-[#f59e0b]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{link.label}</span>
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-px bg-[#f59e0b] transition-transform duration-300 origin-left ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* CTA + mobile toggle */}
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-visit"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 font-body text-xs font-semibold uppercase tracking-[0.18em] rounded-sm bg-[#0a3d7a] text-[#faf6f0] hover:bg-[#f59e0b] hover:text-[#0a3d7a] transition-colors duration-300"
          >
            Visit Us
          </Link>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-sm border border-[rgba(10,61,122,0.2)] text-[#0a3d7a]"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden glass border-t border-[rgba(10,61,122,0.12)] overflow-hidden max-h-[calc(100svh-76px)] overflow-y-auto"
          >
            <nav className="container-x py-8 flex flex-col" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: 0.05 + i * 0.05 }}
                >
                  <NavLink
                    to={link.href}
                    end={link.href === "/"}
                    data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-baseline gap-4 py-3 border-b border-[rgba(10,61,122,0.08)] ${
                        isActive ? "text-[#f59e0b]" : "text-[#0a3d7a]"
                      }`
                    }
                  >
                    <span className="font-label text-[0.6rem] text-[#f59e0b]/80 w-6">
                      0{i + 1}
                    </span>
                    <span className="font-display text-3xl leading-tight">
                      {link.label}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.4 }}
                className="mt-7 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/contact"
                  data-testid="nav-mobile-cta"
                  className="btn-primary"
                >
                  Visit Us
                </Link>
                <a
                  href={`tel:${BRAND.contact.phone.replace(/\s/g, "")}`}
                  data-testid="nav-mobile-phone"
                  className="inline-flex items-center gap-2 font-body text-sm font-semibold text-[#0a3d7a]"
                >
                  <Phone size={15} className="text-[#f59e0b]" />
                  {BRAND.contact.phone}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
